/**
 * Migration registry for device-owned storage (`device:` entries in
 * specs/sync/sync.yaml that declare a `schemaVersion`).
 *
 * Each step maps stored data at version K to version K+1; a payload without
 * a `_v` stamp is version 0 (written before versioning existed). The step
 * interfaces are GENERATED into sync-config.ts from the DSL's schemaVersion —
 * bumping it adds a required key here and the build fails until the step is
 * written. Steps must be total: never throw on missing/unknown fields, and
 * never drop data you don't recognize — device storage has no server copy
 * to re-fetch from. `loadLocalPreferences`' defaults-merge remains the final
 * safety net for fields a migration leaves absent.
 *
 * Spec: docs/specs/mobile/device-storage-migration.mobile.md
 */

import type { PreferencesData, PreferencesMigrations } from '../generated/sync/sync-config';

export const preferencesMigrations: PreferencesMigrations = {
  // v0 → v1: the pre-versioning payload already has the v1 field layout —
  // this step only exists to stamp `_v`. Pass everything through untouched.
  0: (old) => ({ ...old }) as PreferencesData,
  // v1 → v2: client-direct 'azure' TTS was retired. Normalize any stored
  // 'azure' provider to 'azure-server' (the server-synthesis path); leave
  // 'device' and every other field untouched.
  1: (old) => {
    const next = { ...old };
    if (next.ttsProvider === 'azure') next.ttsProvider = 'azure-server';
    return next;
  },
  // v2 → v3: pre-v3, the defaults-merge baked uiLanguage 'target' into every
  // install, so a stored 'target' is indistinguishable from an explicit
  // English choice and OS language detection never ran (bugs.md 2026-07-16).
  // Reset anything that isn't 'source' to '' (= unset → follow the OS);
  // 'source' is kept — it was only ever reachable via the settings toggle.
  // Trade-off: a user who genuinely toggled to 'target' re-runs detection
  // once and may need to toggle again (pre-launch, internal testers only).
  2: (old) => {
    const next = { ...old };
    if (next.uiLanguage !== 'source') next.uiLanguage = '';
    return next as PreferencesData;
  },
};
